import { ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { px } from "@/components/tool-kit/charts/marks";
import { colorIndex, paletteColor } from "@/lib/tool-kit/charts/color";
import {
  type NumberFormat,
  tickFormat,
  valueText,
} from "@/lib/tool-kit/charts/format";
import {
  CHART_FONT_SIZE,
  CHART_HEIGHT,
  CHART_WIDTH,
  cartesianFrame,
} from "@/lib/tool-kit/charts/layout";
import { linearScale, niceTicks } from "@/lib/tool-kit/charts/scale";
import { maxTextWidth } from "@/lib/tool-kit/charts/text";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A histogram from bin edges and counts (`buildHistogram`). The bars touch — the bins
 * are a continuous number line, not separate categories — with a hairline of white
 * between them so each bin can still be told apart. A short run of bins is labelled at
 * every edge, so the reader can see exactly where each bin starts and stops.
 */

/** Label every edge up to this many bins; beyond it, nice ticks. */
const EDGE_LABEL_LIMIT = 16;

export function HistogramChart({
  id,
  edges,
  counts,
  relative,
  edgeDecimals,
  style,
  fmt,
  label,
  description,
}: {
  /** The `<svg>` id, unique on the page. */
  id: string;
  /** n + 1 edges for n bins. */
  edges: number[];
  counts: number[];
  /** Draw each bar's share of the total instead of its count. */
  relative: boolean;
  /** Decimals for the edge labels. */
  edgeDecimals: number;
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
}) {
  const total = counts.reduce((sum, count) => sum + count, 0) || 1;
  const heights = counts.map((count) => (relative ? count / total : count));
  const yAxis = niceTicks(0, Math.max(...heights, relative ? 0.01 : 1), {
    includeZero: true,
    integer: !relative,
  });
  const yTick = tickFormat(fmt, yAxis.decimals);
  const first = edges[0];
  const last = edges.at(-1) ?? first + 1;
  const xTicks =
    edges.length - 1 <= EDGE_LABEL_LIMIT
      ? edges
      : niceTicks(first, last, { exact: true, count: 8 }).ticks;
  const xTick = tickFormat(fmt, edgeDecimals);
  const frame = cartesianFrame({
    width: CHART_WIDTH,
    height: CHART_HEIGHT,
    title: style.apa ? "" : style.title,
    xTitle: style.xTitle,
    yTitle: style.yTitle,
    leftLabelWidth: maxTextWidth(yAxis.ticks.map(yTick), {
      fontSize: CHART_FONT_SIZE.tick,
    }),
    bottomLabelHeight: CHART_FONT_SIZE.tick,
    topPadding: style.valueLabels ? 18 : 8,
  });
  const { plot } = frame;
  const x = linearScale([first, last], [plot.x, plot.x + plot.width]);
  const y = linearScale([yAxis.min, yAxis.max], [plot.y + plot.height, plot.y]);
  const color = paletteColor(colorIndex(style.color), 0);
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitles frame={frame} />
      <ValueAxis
        apa={style.apa}
        format={yTick}
        orient="left"
        plot={plot}
        scale={y}
        ticks={yAxis.ticks}
      />
      <ValueAxis
        apa={style.apa}
        format={xTick}
        grid={false}
        orient="bottom"
        plot={plot}
        scale={x}
        ticks={xTicks}
      />
      {heights.map((height, index) => {
        const left = x(edges[index]);
        const right = x(edges[index + 1]);
        const top = y(height);
        return (
          <g key={edges[index]}>
            <rect
              fill={color}
              height={px(y(0) - top)}
              stroke={CHART_INK.background}
              strokeWidth={1}
              width={px(Math.max(0, right - left))}
              x={px(left)}
              y={px(top)}
            />
            {style.valueLabels && counts[index] > 0 ? (
              <text
                fill={CHART_INK.text}
                fontSize={CHART_FONT_SIZE.value}
                textAnchor="middle"
                x={px((left + right) / 2)}
                y={px(top - 4)}
              >
                {relative ? valueText(fmt, height, 3) : fmt(height)}
              </text>
            ) : null}
          </g>
        );
      })}
    </ChartSvg>
  );
}
